import React, { useEffect, useState, useRef } from "react";
import {
  initialNodeStyle,
  initialUndirectedEdgeStyle,
  visitedNodeStyle,
  visitedUndirectedEdgeStyle,
  visitingNodeStyle,
} from "./GraphStyle";
import "./BFSAlgorithm.css";
import "./Prims.css";

const Prims = ({ cytoscape, nodes, startingNode }) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [distances, setDistances] = useState({});
  const [parentNodes, setParentNodes] = useState({});
  const [visitedSet, setVisitedSet] = useState(new Set());
  const [animationMode, setAnimationMode] = useState("setInitials");
  const [animationCompleted, setAnimationCompleted] = useState(false);

  const distancesArrayRef = useRef({});
  const parentNodesArrayRef = useRef({});

  const handleButtonClick = async () => {
    if (isAnimating) return;

    if (animationMode === "setInitials") {
      setInitials();
      setAnimationMode("animatePrims");
    } else {
      setIsAnimating(true);
      await animatePrims();
      setIsAnimating(false);
      setAnimationMode("setInitials");
      setAnimationCompleted(true);
    }
  };

  const setInitials = () => {
    distancesArrayRef.current = {};
    parentNodesArrayRef.current = {};
    const distancesArray = distancesArrayRef.current;
    const parentNodesArray = parentNodesArrayRef.current;

    for (const node of nodes) {
      distancesArray[node.data.id] = Infinity;
      parentNodesArray[node.data.id] = null;
    }

    const initialVisitedSet = new Set(nodes.map((node) => node.data.id));
    setVisitedSet(initialVisitedSet);

    distancesArray["node" + startingNode] = 0;
    setDistances({ ...distancesArray });
    setParentNodes({ ...parentNodesArray });

    setAnimationMode("animatePrims");
  };

  const animatePrims = async () => {
    const distancesArray = distancesArrayRef.current;
    const parentNodesArray = parentNodesArrayRef.current;

    while (visitedSet.size > 0) {
      //console.log("Visited Set:", [...visitedSet]); // Log the visited set

      const visitedSetCopy = new Set([...visitedSet]);
      let currentNode;

      await new Promise((resolve) => {
        setTimeout(() => {
          currentNode = findMinimumDistanceNode(distancesArray, visitedSetCopy);
          visitedSet.delete(currentNode);
          cytoscape.nodes(`[id='${currentNode}']`).style({
            ...visitedNodeStyle,
          });
          const targetNode = parentNodesArray[currentNode];
          if (targetNode !== null) {
            const edgeId = `[source='${targetNode}'][target='${currentNode}'], [source='${currentNode}'][target='${targetNode}']`;
            cytoscape.edges(edgeId).style({
              ...visitedUndirectedEdgeStyle,
            });
          }
          resolve();
        }, 1000);
      });

      const adjacentEdges = cytoscape
        .edges(`[source='${currentNode}'], [target='${currentNode}']`)
        .filter((edge) => {
          const sourceNode = edge.data("source");
          const targetNode = edge.data("target");
          return (
            visitedSetCopy.has(sourceNode) && visitedSetCopy.has(targetNode)
          );
        });

      for (const edge of adjacentEdges) {
        const sourceNode = edge.data("source");
        const targetNode = edge.data("target");
        const weight = edge.data("weight");

        // Ensure consistent direction for processing
        const processedEdgeId = `${Math.min(sourceNode, targetNode)}-${Math.max(
          sourceNode,
          targetNode
        )}`;
        //console.log("Processing edge:", processedEdgeId);

        // Process the edge based on the target node
        const processedTargetNode =
          currentNode === sourceNode ? targetNode : sourceNode;

        if (weight < distancesArray[processedTargetNode]) {
          distancesArray[processedTargetNode] = weight;
          parentNodesArray[processedTargetNode] = currentNode;

          setDistances({ ...distancesArray });
          setParentNodes({ ...parentNodesArray });

          await new Promise((resolve) => {
            setTimeout(resolve, 2000);
          });
        }
      }
    }

    setIsAnimating(false);
    setAnimationMode("setInitials");
  };

  const findMinimumDistanceNode = (distancesArray, visitedSet) => {
    let minNode = null;
    let minDistance = Infinity;

    for (const nodeId of visitedSet) {
      if (distancesArray[nodeId] < minDistance) {
        minNode = nodeId;
        minDistance = distancesArray[nodeId];
      }
    }

    return minNode;
  };

  const resetAnimation = () => {
    if (isAnimating) return;

    cytoscape.elements("node").style(initialNodeStyle);

    cytoscape.elements("edge").style(initialUndirectedEdgeStyle);

    setAnimationMode("setInitials");
    setDistances([]);
    setParentNodes([]);
    setVisitedSet([]);

    setIsAnimating(false);
    setAnimationCompleted(false);
  };

  return (
    <div>
      <div className="button-container">
        <button
          className="animate-reset-button"
          onClick={handleButtonClick}
          disabled={isAnimating || animationCompleted}
        >
          {isAnimating
            ? "Animating..."
            : animationMode === "setInitials"
            ? "Set Initials"
            : "Start Animation"}
        </button>
        &nbsp;&nbsp;
        <button
          className="animate-reset-button"
          onClick={resetAnimation}
          disabled={isAnimating}
        >
          Reset Animation
        </button>
      </div>
      <div className="table-container">
        <table className="custom-table">
          <tbody>
            <tr>
              <th>Node</th>
              {Object.keys(distances).map((nodeId) => (
                <td key={nodeId}>{nodeId}</td>
              ))}
            </tr>
            <tr>
              <th>Distance</th>
              {Object.keys(distances).map((nodeId) => (
                <td key={nodeId}>{distances[nodeId]}</td>
              ))}
            </tr>
            <tr>
              <th>Parent Node</th>
              {Object.keys(parentNodes).map((nodeId) => (
                <td key={nodeId}>
                  {parentNodes[nodeId] !== null ? parentNodes[nodeId] : "null"}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Prims;
